import { useEffect, useRef, useState } from "react";
import { NumberField } from "../NumberField";
import { FORMATION_LIST, type FormationKey } from "../formations";
import { sourceLabel, type Source } from "../source";
import { gradientCss } from "../gradient";
import { TRANSITIONS, transDef, sampleAt, type SceneLook, type TransitionKind } from "../sequencer";
import type { useSequencer } from "../hooks/useSequencer";
import { fmt } from "../util";

type Props = {
  sq: ReturnType<typeof useSequencer>;
  studioSource: Source; // the current studio shape, for "capture"
  studioLook: SceneLook; // the current studio colouring, captured with it
  textInput: string;
};

/** CSS background for a look swatch (empty string = native colours). */
function lookCss(look: SceneLook): string {
  if (look.color === "solid") return look.hex;
  if (look.color === "gradient") return gradientCss(look.stops);
  return "";
}

/** The show-builder: a draggable timeline of scenes + transition presets. */
export function Timeline({ sq, studioSource, studioLook, textInput }: Props) {
  const {
    timeline, timing, playing, setPlaying, loop, setLoop, playhead, nowLabel, seek,
    selected, setSelected, addScene, removeScene, duplicateScene, moveScene, moveSceneTo,
    setSceneSource, setSceneLook, setSceneHold, setTransitionKind, setTransitionDuration, save, load,
  } = sq;

  const [dragOver, setDragOver] = useState<number | null>(null);
  const [cardOver, setCardOver] = useState<number | null>(null);
  const [nowScene, setNowScene] = useState(-1);
  const scrubRef = useRef<HTMLInputElement | null>(null);
  const rulerRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);

  // reflect the live playhead onto the scrub bar + ruler + playing-scene highlight
  useEffect(() => {
    const id = window.setInterval(() => {
      const s = scrubRef.current;
      if (s && document.activeElement !== s) s.value = String(playhead.current);
      if (headRef.current && timing.total > 0) {
        headRef.current.style.left = ((playhead.current / timing.total) * 100).toFixed(2) + "%";
      }
      const st = sampleAt(timeline, playhead.current);
      setNowScene(st.kind === "hold" ? st.scene : st.kind === "trans" ? st.to : -1);
    }, 100);
    return () => window.clearInterval(id);
  }, [timeline, timing.total, playhead]);

  function applyText(id: string) {
    setSceneSource(id, { kind: "text", text: textInput.trim() || " ", font: "Arial" });
  }

  return (
    <div className="seq">
      <div className="seq-head">
        <button className="chip play" onClick={() => setPlaying((p) => !p)}>{playing ? "❚❚" : "►"}</button>
        <span className="clock">{nowLabel}</span>
        <input
          ref={scrubRef}
          className="scrub"
          type="range"
          min={0}
          max={Math.max(0.1, timing.total)}
          step={0.02}
          defaultValue={0}
          onInput={(e) => seek(+e.currentTarget.value)}
        />
        <span className="clock dim">{fmt(timing.total)}</span>
        <button className={"chip toggle" + (loop ? " active" : "")} onClick={() => setLoop((l) => !l)}>↻ Loop</button>
        <span className="seq-sep" />
        <button
          className="chip go"
          title={"Add a scene with the studio's current shape AND colour (" + sourceLabel(studioSource) + ")"}
          onClick={() => addScene(studioSource, studioLook)}
        >
          ⊕ Capture studio look
        </button>
        <button className="chip" onClick={save}>⭳ Save</button>
        <label className="chip as-file">⭱ Load<input type="file" accept=".json" hidden onChange={load} /></label>
      </div>

      {/* proportional time ruler: holds + transitions at true scale; click to seek */}
      <div
        ref={rulerRef}
        className="seq-ruler"
        title="Click to seek"
        onClick={(e) => {
          const r = rulerRef.current?.getBoundingClientRect();
          if (r && timing.total > 0) seek(((e.clientX - r.left) / r.width) * timing.total);
        }}
      >
        {timing.segments.map((seg, i) => {
          const w = timing.total > 0 ? ((seg.end - seg.start) / timing.total) * 100 : 0;
          const label = seg.type === "hold"
            ? sourceLabel(timeline.scenes[seg.index]?.source ?? { kind: "formation", key: "sphere" })
            : transDef(seg.kind).label;
          return (
            <div
              key={i}
              className={"ruler-seg " + seg.type + (seg.type === "hold" && nowScene === seg.index ? " playing" : "")}
              style={{ width: w.toFixed(3) + "%" }}
              title={label + " · " + fmt(seg.start) + "–" + fmt(seg.end)}
            />
          );
        })}
        <div ref={headRef} className="ruler-head" />
      </div>

      {/* draggable transition presets */}
      <div className="seq-palette">
        <span className="slider-label">Transitions — drag onto a gap:</span>
        {TRANSITIONS.map((t) => (
          <button
            key={t.kind}
            className="trans-chip"
            draggable
            onDragStart={(e) => e.dataTransfer.setData("text/trans", t.kind)}
            title={t.blurb}
          >
            <span className="trans-glyph">{t.glyph}</span> {t.label}
          </button>
        ))}
      </div>

      {/* the track: scene · gap · scene · gap · … */}
      <div className="seq-track">
        {timeline.scenes.map((sc, i) => {
          const isFormation = sc.source.kind === "formation";
          const swatch = lookCss(sc.look);
          return (
            <div key={sc.id} className="seq-lane">
              <div
                className={"scene-card" + (selected === sc.id ? " selected" : "") + (nowScene === i ? " playing" : "") + (cardOver === i ? " dragover" : "")}
                onClick={() => setSelected(sc.id)}
                draggable
                onDragStart={(e) => e.dataTransfer.setData("text/scene", sc.id)}
                onDragOver={(e) => { if (e.dataTransfer.types.includes("text/scene")) { e.preventDefault(); setCardOver(i); } }}
                onDragLeave={() => setCardOver((d) => (d === i ? null : d))}
                onDrop={(e) => {
                  const id = e.dataTransfer.getData("text/scene");
                  if (id) { e.preventDefault(); moveSceneTo(id, i); }
                  setCardOver(null);
                }}
              >
                <div className="scene-top">
                  <span className="scene-idx">{i + 1}</span>
                  <span className="scene-label" title={sourceLabel(sc.source)}>{sourceLabel(sc.source)}</span>
                  {swatch && <span className="look-dot" style={{ background: swatch }} title={"scene colour" + (sc.look.color === "solid" ? " " + sc.look.hex : " gradient")} />}
                  <button className="scene-x" title="Remove" onClick={(e) => { e.stopPropagation(); removeScene(sc.id); }}>✕</button>
                </div>
                <select
                  className="scene-src"
                  value={isFormation ? (sc.source as { key: FormationKey }).key : ""}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setSceneSource(sc.id, { kind: "formation", key: e.target.value as FormationKey })}
                >
                  {!isFormation && <option value="">{sc.source.kind}…</option>}
                  {FORMATION_LIST.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
                <div className="scene-actions">
                  <button className="mini" title="Set to typed text" onClick={(e) => { e.stopPropagation(); applyText(sc.id); }}>abc</button>
                  <button className="mini" title="Capture the studio shape + colour into this scene" onClick={(e) => { e.stopPropagation(); setSceneSource(sc.id, studioSource, studioLook); }}>◎</button>
                  <button className="mini" title="Paint this scene with the studio's current colour" onClick={(e) => { e.stopPropagation(); setSceneLook(sc.id, studioLook); }}>🎨</button>
                  <button className="mini" title="Move left" onClick={(e) => { e.stopPropagation(); moveScene(sc.id, -1); }}>◀</button>
                  <button className="mini" title="Move right" onClick={(e) => { e.stopPropagation(); moveScene(sc.id, 1); }}>▶</button>
                  <button className="mini" title="Duplicate" onClick={(e) => { e.stopPropagation(); duplicateScene(sc.id); }}>⧉</button>
                </div>
                <NumberField label="Hold" value={sc.hold} min={0} max={30} step={0.5} onChange={(v) => setSceneHold(sc.id, v)} format={(v) => v.toFixed(1) + "s"} />
              </div>

              {i < timeline.scenes.length - 1 && (
                <div
                  className={"trans-slot" + (dragOver === i ? " over" : "")}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
                  onDragLeave={() => setDragOver((d) => (d === i ? null : d))}
                  onDrop={(e) => {
                    e.preventDefault();
                    const k = e.dataTransfer.getData("text/trans") as TransitionKind;
                    if (k) setTransitionKind(i, k);
                    setDragOver(null);
                  }}
                  title="Drop a transition preset here"
                >
                  {/* click-to-cycle lives on the glyph+name ONLY — a slot-wide
                      handler also caught the duration stepper's clicks */}
                  <span
                    className="trans-pick"
                    title="Click to cycle the transition"
                    onClick={() => {
                      const cur = TRANSITIONS.findIndex((t) => t.kind === timeline.transitions[i]?.kind);
                      setTransitionKind(i, TRANSITIONS[(cur + 1) % TRANSITIONS.length].kind);
                    }}
                  >
                    <span className="trans-glyph">{transDef(timeline.transitions[i]?.kind ?? "morph").glyph}</span>
                    <span className="trans-name">{transDef(timeline.transitions[i]?.kind ?? "morph").label}</span>
                  </span>
                  <NumberField
                    label=""
                    value={timeline.transitions[i]?.duration ?? 1.5}
                    min={0.2}
                    max={8}
                    step={0.1}
                    onChange={(v) => setTransitionDuration(i, v)}
                    format={(v) => v.toFixed(1) + "s"}
                  />
                </div>
              )}
            </div>
          );
        })}

        <button className="scene-add" title="Add a scene" onClick={() => addScene({ kind: "formation", key: "sphere" })}>＋<span>Scene</span></button>
      </div>
    </div>
  );
}
